import React, { useState, useEffect } from "react";
import {
  Link as RouterLink,
  useParams,
  useNavigate,
  Link,
} from "react-router-dom";
import * as formik from "formik";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { Col } from "react-bootstrap";
import Swal from "sweetalert2";
import axiosClient from "../../../config/axios";
import { orderValidationSchema } from "./orderVialFieldsYupValidation";
import { useAuth } from "../../../context/AuthContext";
import moment from "moment-timezone";

// Componentes del formulario
import { Panel, PanelHeader, PanelBody } from "../../../components/panel/panel";
import InfringementData from "./formOrderVial/InfringementData";
import OrderVialVehiclesForm from "./formOrderVial/orderVialVehiclesForm";
import ServiceDetails from "./formOrderVial/serviceDetails";
import VehicleEquipmentDetails from "./formOrderVial/vehicleEquipmentDetails";
import CraneData from "./formOrderVial/craneData";
import PaymentData from "./formOrderVial/paymentData";
import Loader from "../../../components/loader/index.js";
import { useCommon } from "../../../context/CommonContext.js";
import AccordionPayments from "./formOrderVial/accordionPayments.js";
import { useRef } from "react";
import Button from "react-bootstrap/Button";

function formatNumberWithCommas(number) {
  if (number) {
    // const roundedNumber = parseFloat(number);
    const roundedNumber = parseFloat(number).toFixed(2);
    return roundedNumber.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  return "";
}

function EditOrderVial() {
  const { id } = useParams(); // ID de la orden
  const [initialValues, setInitialValues] = useState(null);
  const { Formik } = formik;
  const navigate = useNavigate();
  const { token, userId, fullName, username, roles, isAdmin, isAccountant } =
    useAuth();

  const { loading, setLoading } = useCommon();
  const [paymentTypes, setPaymentTypes] = useState([]);
  const tablePaymentsContadoRef = useRef();
  const [isCancelling, setIsCancelling] = useState(false);
  const { canCancelVaroExpress } = useAuth();
  const [shouldPrintTicket, setShouldPrintTicket] = useState(false);

  function isPaymentCovered(values) {
    const paymentsContado = values.payments_contado || [];
    const paymentsCredito = values.payments_credito || [];
    const costosAdicionales = values.montosExtras.reduce((acc, item) => {
      const monto = parseFloat(item.monto_extra_monto);
      return acc + (isNaN(monto) ? 0 : monto);
    }, 0);

    const total = parseFloat(values.subtotal || 0) + costosAdicionales;

    const sumContado = paymentsContado.reduce(
      (acc, p) => acc + parseFloat(p.payment_amount || 0),
      0
    );
    const sumCredito = paymentsCredito.reduce(
      (acc, p) => acc + parseFloat(p.payment_amount || 0),
      0
    );

    return sumContado + sumCredito >= total;
  }

  useEffect(() => {
    axiosClient
      .get("/paymentTypes/all")
      .then((response) => {
        const formattedOptions = response.data.data.map((paymentType) => ({
          value: paymentType.id,
          label: paymentType.payment_type_name,
          code: paymentType.payment_type_code,
          id: paymentType.id,
        }));
        setPaymentTypes(formattedOptions);
      })
      .catch((error) => {
        console.error("Error fetching paymentTypes:", error);
      });
  }, []);

  const formatDateTimeLocal = (dateString) => {
    if (!dateString) return "";
    return moment.utc(dateString).format("YYYY-MM-DDTHH:mm");
  };

  useEffect(() => {
    setLoading(true);
    axiosClient
      .get(`/order-vial/${id}`)
      .then(({ data }) => {
        const order = data;
        const vehiculoNumero = order.vehiculoNumero || 1;

        // Transformar vehicles a vehiculos
        const vehiculos = order.vehicles.map((vehiculo, index) => ({
          vehiculoNumero: vehiculoNumero,
          placas: vehiculo.placas || "",

          anio: vehiculo.anio
            ? {
                label: vehiculo.anio.toString(),
                value: vehiculo.anio,
              }
            : null,

          vehicle_type_id: vehiculo.VehicleModel?.VehicleType
            ? {
                label: vehiculo.VehicleModel.VehicleType.vehicle_type_name,
                value: vehiculo.VehicleModel.VehicleType.id,
              }
            : null,

          brand_id: vehiculo.VehicleModel?.Brand
            ? {
                label: vehiculo.VehicleModel.Brand.brand_name,
                value: vehiculo.VehicleModel.Brand.id,
              }
            : null,

          vehicle_model_id: vehiculo.VehicleModel
            ? {
                label: vehiculo.VehicleModel.model_name,
                value: vehiculo.VehicleModel.id,
              }
            : null,

          color_id: vehiculo.Color
            ? {
                label: vehiculo.Color.color_name,
                value: vehiculo.Color.id,
                color: vehiculo.Color.color_code,
                colorContrast: vehiculo.Color.color_contrast,
              }
            : null,

          shielding_type_id: vehiculo.ShieldingType
            ? {
                label: vehiculo.ShieldingType.shielding_type,
                value: vehiculo.ShieldingType.id,
              }
            : null,

          numero_serie: vehiculo.numero_serie || "",
          observaciones: vehiculo.observaciones || "",
          registrar_volumen: vehiculo.registrar_volumen || false,
          carga_kg: vehiculo.carga_kg || "",
          volumen_largo: vehiculo.volumen_largo || null,
          volumen_ancho: vehiculo.volumen_ancho || null,
          volumen_altura: vehiculo.volumen_altura || null,
          volumen_m3: vehiculo.volumen_m3 || null,
          estado_llantas_giran: vehiculo.estado_llantas_giran ?? null,
          estado_volante_gira: vehiculo.estado_volante_gira ?? null,
          estado_pie_calle: vehiculo.estado_pie_calle ?? null,
          estado_neutral: vehiculo.estado_neutral ?? null,
          estado_en_cajon: vehiculo.estado_en_cajon ?? null,
          asistencia_paso_corriente: vehiculo.asistencia_paso_corriente ?? null,
          asistencia_combustible: vehiculo.asistencia_combustible ?? null,
          asistencia_cambio_neumatico:
            vehiculo.asistencia_cambio_neumatico ?? null,
          asistencia_cerrajeria: vehiculo.asistencia_cerrajeria ?? null,
          // Aquí puedes mapear más campos según lo necesites
        }));

        // Asigna los valores precargados

        setInitialValues({
          ...order,
          vehiculos,

          crane_id: order.Crane
            ? {
                value: order.Crane.id,
                label: `#${order.Crane.crane_number}, ${order.Crane.description}`,
              }
            : null,

          operator_id: order.operator
            ? {
                value: order.operator.id,
                label: `${order.operator.name} ${order.operator.first_surname} ${order.operator.second_surname}`,
              }
            : null,

          estatus: order.estatus || "Pendiente",
          subtotal: order.subtotal || "",
          montosExtras: order.montosExtras || [],
          notas: order.notas || "",
          extinguidor: !!order.extinguidor,
          llanta_refaccion: !!order.llanta_refaccion,
          gato: !!order.gato,
          herramientas: !!order.herramientas,
          cable_pasa_corriente: !!order.cable_pasa_corriente,
          senalamientos: !!order.senalamientos,
          otros: !!order.otros,
          otros_observaciones: order.otros_observaciones || "",
          fecha_hora_orden: formatDateTimeLocal(order.fecha_hora_orden),
          genera_costo_por_estadia: !!order.genera_costo_por_estadia,
        });
      })
      .catch((error) => {
        console.error("Error al obtener la orden:", error);
        Swal.fire("Error", "No se pudo cargar la orden", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  if (loading || !initialValues) return <Loader isLoading={true} />;

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Inicio</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/orders-vial">Varo Express</Link>
            </li>
            <li className="breadcrumb-item active">Editar orden</li>
          </ol>
          <h1 className="page-header mb-0">Editar Orden - Varo Express</h1>
        </div>
      </div>
      <Formik
        validationSchema={orderValidationSchema}
        initialValues={initialValues}
        enableReinitialize
        validateOnChange
        onSubmit={(values, { setSubmitting, setFieldValue }) => {
          setSubmitting(true);

          const paymentsContado = values.payments_contado || [];
          const paymentsCredito = values.payments_credito || [];
          const invoicesContado = values.invoices_contado || [];
          const invoicesCredito = values.invoices_credito || [];

          const costosAdicionales = values.montosExtras.reduce((acc, item) => {
            const monto = parseFloat(item.monto_extra_monto);
            return acc + (isNaN(monto) ? 0 : monto);
          }, 0);

          const total = parseFloat(values.subtotal || 0) + costosAdicionales;

          // Suma de pagos
          const sumContado = paymentsContado.reduce(
            (acc, p) => acc + parseFloat(p.payment_amount || 0),
            0
          );
          const sumCredito = paymentsCredito.reduce(
            (acc, p) => acc + parseFloat(p.payment_amount || 0),
            0
          );

          // Verifica si alguno coincide con el total
          // const estatus =
          //   sumContado >= total ||
          //   sumCredito >= total ||
          //   sumContado + sumCredito >= total
          //     ? "Pagado"
          //     : values.estatus; // mantiene el estatus actual si no cumple
          // //: "Pendiente";

          //Si el estatus está en cancelado NO se cambia a pagado o a pendiente si se cubre el monto
          //Si el estatus NO esta Cancelado y se cubre el monto entonces se pasa a Pagado
          //Si el estatus NO esta Cancelado y NO se cubre el monto entonces se pasa a Pendiente
          // Verifica si alguno coincide con el total y si no está Cancelado
          const estatus =
            values.estatus === "Cancelado"
              ? "Cancelado"
              : sumContado >= total ||
                sumCredito >= total ||
                sumContado + sumCredito >= total
              ? "Pagado"
              : "Pendiente";


          // Actualiza el formik state
          //setFieldValue("estatus", estatus);

          const formattedValues = {
            ...values,
            crane_id: values.crane_id?.value || null,
            operator_id: values.operator_id?.value || null,

            vehicles: values.vehiculos.map((v) => ({
              ...v,
              vehicle_model_id:
                v.vehicle_model_id?.value || v.vehicle_model_id || null,
              color_id: v.color_id?.value || v.color_id || null,
              shielding_type_id:
                v.shielding_type_id?.value || v.shielding_type_id || null,
              anio: v.anio?.value || v.anio || null,
            })),
            vehiculoNumero: values.vehiculos[0]?.vehiculoNumero
              ? parseInt(values.vehiculos[0].vehiculoNumero, 10)
              : null,
            costosAdicionales: parseFloat(costosAdicionales.toFixed(2)),
            total: parseFloat(total.toFixed(2)),
            subtotal: 0,
            grua: true,
            estatus,
          };

          const invoicesPayload = {
            invoices: [
              ...invoicesContado.map((inv) => ({
                ...inv,
                invoice_amount: parseFloat(inv.invoice_amount),
              })),
              ...invoicesCredito.map((inv) => ({
                ...inv,
                invoice_amount: parseFloat(inv.invoice_amount),
              })),
            ],
          };

          const paymentsPayload = {
            payments: [
              ...paymentsContado.map((inv) => ({
                ...inv,
                payment_amount: parseFloat(inv.payment_amount),
              })),
              ...paymentsCredito.map((inv) => ({
                ...inv,
                payment_amount: parseFloat(inv.payment_amount),
              })),
            ],
          };

          //console.log("Valores del formulario a enviar:xd", formattedValues);
          //console.log("Valores del formulario a enviar pagos: ", paymentsPayload);

          axiosClient
            .put(`/order-vial/${id}`, formattedValues)
            .then(() => {
              return axiosClient.post(
                `/invoicesOrderVial/saveUpdateMultiple/${id}`,
                invoicesPayload
              );
            })
            .then(() => {
              return axiosClient.post(
                `/paymentsOrderVial/saveUpdateMultiple/${id}`,
                paymentsPayload
              );
            })
            .then(() => {
              setFieldValue("estatus", estatus);
              Swal.fire("Éxito", "La orden fue actualizada", "success");
            })
            .then(() => {
              if (shouldPrintTicket) {
                const ticketPayload = {
                  orderVialId: id,
                  datos: formattedValues,
                  pagos: paymentsPayload.payments,
                  facturas: invoicesPayload.invoices,
                };

                // console.log(
                //   "Payload que se enviará para imprimir ticket:",
                //   ticketPayload
                // );

                return axiosClient
                  .post("/order-vial/emitir-ticket", {
                    payload: ticketPayload,
                  })
                  .then(() => {
                    Swal.fire("Éxito", "Imprimiendo ticket", "success");
                  })
                  .catch((err) => {
                    console.error("Error al emitir el ticket:", err);
                    Swal.fire(
                      "Advertencia",
                      "La orden se guardó, pero no se pudo imprimir el ticket.",
                      "warning"
                    );
                  });
              }
            })
            .catch((err) => {
              console.error("Error al actualizar pagos:", err);
              const errorMessage =
                err.response?.data?.error || "Ha ocurrido un error inesperado";

              Swal.fire({
                title: "Ups!",
                text: errorMessage,
                icon: "error",
                confirmButtonText: "Cerrar",
                cancelButtonText: "Cancelar",
                showCancelButton: false,
              });
              setSubmitting(false);
            })
            .finally(() => {
              setSubmitting(false);
            });
        }}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          errors,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Loader isLoading={isSubmitting} />

            <Row className="mb-3">
              <Col md="12">
                <Panel>
                  <PanelHeader noButton>Datos de la orden</PanelHeader>
                  <PanelBody>
                    <InfringementData
                      values={values}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                    />
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader noButton>Datos del vehículo</PanelHeader>
                  <PanelBody>
                    <OrderVialVehiclesForm
                      token={token}
                      values={values}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      touched={touched}
                      errors={errors}
                    />
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader noButton>Datos del servicio</PanelHeader>
                  <PanelBody>
                    <ServiceDetails
                      values={values}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                    />
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader noButton>Detalles del equipamiento</PanelHeader>
                  <PanelBody>
                    <VehicleEquipmentDetails
                      values={values}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                    />
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader noButton>Datos de operación</PanelHeader>
                  <PanelBody>
                    <CraneData
                      values={values}
                      errors={errors}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                    />
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader noButton>Datos de total a pagar</PanelHeader>
                  <PanelBody>
                    <PaymentData
                      values={values}
                      errors={errors}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                    />
                  </PanelBody>
                </Panel>

                <AccordionPayments
                  values={values}
                  formatNumberWithCommas={formatNumberWithCommas}
                  setPaymentTypes={setPaymentTypes}
                  paymentTypes={paymentTypes}
                  isAccountant={isAccountant}
                  isAdmin={isAdmin}
                />

                <div
                  className="d-flex justify-content-between mt-4"
                  style={{ minHeight: "38px" }}
                >
                  <div
                    style={{
                      visibility:
                        canCancelVaroExpress() && values.estatus !== "Cancelado"
                          ? "visible"
                          : "hidden",
                    }}
                  >
                    <Button
                      variant="danger"
                      disabled={isCancelling}
                      onClick={async () => {
                        const result = await Swal.fire({
                          title: "¿Estás seguro?",
                          text: "Esta acción cancelará la orden.",
                          icon: "warning",
                          showCancelButton: true,
                          confirmButtonColor: "#d33",
                          cancelButtonColor: "#3085d6",
                          confirmButtonText: "Sí, cancelar",
                          cancelButtonText: "No, mantener",
                        });

                        if (!result.isConfirmed) return;

                        setIsCancelling(true);
                        try {
                          await axiosClient.put(`/order-vial/cancel/${id}`);
                          setFieldValue("estatus", "Cancelado");

                          await Swal.fire(
                            "Cancelado",
                            "La orden ha sido cancelada.",
                            "success"
                          );
                        } catch (error) {
                          console.error("Error al cancelar orden:", error);
                          const message =
                            error.response?.data?.error ||
                            "No se pudo cancelar la orden.";
                          await Swal.fire("Error", message, "error");
                        } finally {
                          setIsCancelling(false);
                        }
                      }}
                    >
                      Cancelar orden
                    </Button>
                  </div>

                  <div className="d-flex">
                    <Button
                      type="submit"
                      disabled={isSubmitting || !isPaymentCovered(values)}
                      className="me-2"
                      onClick={() => setShouldPrintTicket(true)} // guardar e imprimir
                    >
                      {isSubmitting
                        ? "Guardando..."
                        : "Guardar e imprimir ticket"}
                    </Button>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="me-2"
                      onClick={() => setShouldPrintTicket(false)}
                    >
                      {isSubmitting ? "Guardando..." : "Guardar cambios"}
                    </Button>
                    <RouterLink to="/orders-vial" className="btn btn-info">
                      Regresar
                    </RouterLink>
                  </div>
                </div>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default EditOrderVial;
